import { DOCUMENT } from '@angular/common';
import {
  Component,
  effect,
  inject,
  NgZone,
  type OnDestroy,
  type OnInit,
  signal,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatToolbar } from '@angular/material/toolbar';
import { ActivatedRoute, NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { of, type Subscription } from 'rxjs';
import { filter, startWith, switchMap, tap } from 'rxjs/operators';

import { ContestRole, SystemRole } from '../../../models/User';
import { AuthService } from '../../services/auth.service';
import { ContestService } from '../../services/contest.service';
import { ContextService } from '../../services/context.service';
import { ContextualHelpService } from '../../services/contextual-help.service';
import { I18nUtil } from '../../services/i18n.util';
import { ThemeService } from '../../services/theme.service';
import { AppFooterComponent } from '../app-footer/app-footer.component';
import { BreadcrumbComponent } from '../breadcrumb/breadcrumb.component';
import { LanguageSwitcherComponent } from '../language-switcher/language-switcher';
import { NotificationPanelComponent } from '../notification-panel/notification-panel.component';
import { ThemePickerComponent } from '../theme-picker/theme-picker.component';

@Component({
  selector: 'app-wrapper',
  standalone: true,
  imports: [
    RouterOutlet,
    RouterLink,
    BreadcrumbComponent,
    AppFooterComponent,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatToolbar,
    ThemePickerComponent,
    LanguageSwitcherComponent,
    NotificationPanelComponent,
    MatDividerModule,
    TranslocoPipe,
  ],
  templateUrl: './wrapper.component.html',
  styleUrl: './wrapper.component.scss',
})
export class WrapperComponent implements OnInit, OnDestroy {
  authService = inject(AuthService);
  themeService = inject(ThemeService);
  i18n = inject(I18nUtil);
  contextualHelpService = inject(ContextualHelpService);

  // How far the toolbar has slid up out of view (0 = fully shown, -toolbarHeight =
  // fully hidden) — tracks the scroll delta 1:1 instead of snapping to a binary
  // shown/hidden state, so it visually follows the scroll gesture as it happens.
  readonly headerOffsetPx = signal(0);

  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private contestService = inject(ContestService);
  private contextService = inject(ContextService);
  private document = inject(DOCUMENT);
  private ngZone = inject(NgZone);
  private routerSub: Subscription | undefined;
  private lastScrollTop = 0;

  // Keeps currentPageHelpKey correct even when it changes independently of navigation
  // (e.g. score-pool resolving its ScoringType-specific key after data loads).
  private readonly pageHelpKeyOverrideEffect = effect(() => {
    this.contextualHelpService.pageHelpKeyOverride();
    this.contextualHelpService.setCurrentPageHelpKey(this.resolveHelpKey());
  });

  ngOnInit(): void {
    if (typeof window !== 'undefined') {
      // Capture phase: the app scrolls inside mat-sidenav-content, not window,
      // but a capturing listener on window still sees its scroll events.
      // Registered outside Angular's zone: the handler only writes signals/CSS
      // vars nothing here reads through template bindings, and the real app
      // (unlike its zoneless-by-default TestBed) runs zone-based CD, so a
      // plain zone-patched listener would trigger a full app-wide change-
      // detection pass on every scroll tick for no reason.
      this.ngZone.runOutsideAngular(() => {
        window.addEventListener('scroll', this.onScroll, true);
      });
    }

    this.routerSub = this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        tap(() => {
          // Router's scrollPositionRestoration resets window's scroll, not the
          // actual scroll container (mat-sidenav-content) this app scrolls inside —
          // so it never fires a 'scroll' event here. Without this, navigating away
          // from a page scrolled down (header hidden) leaves the header stuck
          // hidden — and the breadcrumb's matching shrunk margin — on the new page.
          this.lastScrollTop = 0;
          this.setHeaderOffset(0);

          const helpKey = this.resolveHelpKey();

          // Track which doc actually matches the current page — the drawer uses this
          // to tell when its spotlight links have nothing on-screen to target.
          this.contextualHelpService.setCurrentPageHelpKey(helpKey);

          // Keep the help drawer's content in sync with the page it's now open on,
          // instead of leaving it stuck on whatever page it was opened from.
          if (this.contextualHelpService.isOpen()) {
            this.contextualHelpService.open(helpKey);
          }
        }),
        startWith(null), // Fire immediately on load
        switchMap(() => {
          // Dancers and Judges have their contest stored in the session
          const storedContest = this.authService.getContest();
          if (storedContest) {
            return of(storedContest);
          }

          // For other roles, the contest is determined by the route
          let contestId: string | null = null;
          let current: ActivatedRoute | null = this.route.root;
          while (current?.firstChild) {
            current = current.firstChild;
          }
          while (current) {
            if (current.snapshot.paramMap.has('contestId')) {
              contestId = current.snapshot.paramMap.get('contestId');
              break;
            }
            current = current.parent;
          }

          if (contestId) {
            return this.contestService.getContestById(contestId);
          }

          return of(null);
        }),
      )
      .subscribe((contest) => {
        if (contest) {
          this.themeService.setContestTheme(contest);
        } else {
          this.themeService.clearContestTheme();
        }
      });
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
    if (typeof window !== 'undefined') {
      window.removeEventListener('scroll', this.onScroll, true);
    }
  }

  toggleHelp(): void {
    if (this.contextualHelpService.isOpen()) {
      this.contextualHelpService.close();
    } else {
      this.contextualHelpService.open(this.resolveHelpKey());
    }
  }

  logout(): void {
    this.authService.logout();
    this.router.navigateByUrl('');
  }

  private onScroll = (event: Event): void => {
    // The capture-phase listener on window sees every scroll event on the page,
    // not just the main content area — the help drawer's own content pane, tables,
    // and dialogs all scroll independently (own overflow-y: auto). Without this
    // filter, scrolling any of those corrupts lastScrollTop against an unrelated
    // element and makes the header drift out of sync with actual page scroll
    // distance (e.g. mousing over a scrollable table mid-gesture).
    const target = event.target as HTMLElement | null;
    if (target?.tagName?.toLowerCase() !== 'mat-sidenav-content') {
      return;
    }

    const scrollTop = target.scrollTop;
    const delta = scrollTop - this.lastScrollTop;
    this.lastScrollTop = scrollTop;

    if (scrollTop <= 0) {
      this.setHeaderOffset(0);
      return;
    }

    // Matches the toolbar's own responsive height (see wrapper.component.scss).
    const toolbarHeight = typeof window !== 'undefined' && window.innerWidth < 600 ? 56 : 64;

    // Move by exactly the scroll delta (clamped), so the toolbar tracks the
    // gesture 1:1 instead of snapping to fully hidden/shown at a threshold.
    this.setHeaderOffset(Math.min(0, Math.max(-toolbarHeight, this.headerOffsetPx() - delta)));
  };

  private setHeaderOffset(offsetPx: number): void {
    this.headerOffsetPx.set(offsetPx);
    // Lets fixed-position content below the toolbar (breadcrumb, help drawer) shrink
    // its reserved space by the same amount as the toolbar has slid up, instead of
    // leaving a dead gap once the toolbar has scrolled out of view.
    this.document.documentElement.style.setProperty('--nav-header-offset', `${offsetPx}px`);
  }

  private resolveHelpKey(): string {
    let current: ActivatedRoute | null = this.route.root;
    while (current?.firstChild) {
      current = current.firstChild;
    }
    let helpKey = (current?.snapshot?.data?.['helpKey'] as string | undefined) ?? '';

    // The 'contests' list route loads ParticipantListComponent instead of ContestListComponent
    // for SystemRole.User, so its static route data can't reflect the right help content.
    if (helpKey === 'contest-list' && this.authService.getSystemRole() === SystemRole.User) {
      helpKey = 'participant-list';
    }

    // A page can override the route-resolved key with a more specific one
    // (e.g. score-pool varying its key by the active ScoringType).
    const override = this.contextualHelpService.pageHelpKeyOverride();

    return override || helpKey || this.defaultRoleHelpKey();
  }

  // Falls back to the user's own contest role (which can differ per contest) so the
  // general role guide preselects the role that's actually theirs, instead of always Organizer.
  private defaultRoleHelpKey(): string {
    const role = this.contextService.currentParticipantRole() ?? ContestRole.Organizer;
    return `role-${role.toLowerCase()}`;
  }
}
